// Catalog admin — list products (already-proxied, read-only `GET
// /api/products`) and, since #260, edit a product's price/reorder point and
// read its price-history through the BFF's new `/api/admin/products/*`
// route (web-bff `Proxy/BffProxy.cs`), which forwards to catalog-service's
// `PUT .../price`, `PUT .../reorder-point`, `GET .../price-history`.
//
// Fetch-wrapper shape copied from storefront-ui's `api.ts` (`placeOrder`):
// network failure -> "error", 401 -> "unauthorized" (send back through
// sign-in), defensive JSON parse, non-2xx pulls `payload.error`. Unlike
// storefront-ui's `fetchCatalog`, there's no bundled-JSON fallback here —
// live admin data has no sane static stand-in, so a failure just surfaces.

export interface Product {
  sku: string;
  name: string;
  category: string;
  unitPrice: number;
  reorderPoint: number;
}

export interface PriceHistoryEntry {
  effectiveFrom: string;
  unitPrice: number;
  previousUnitPrice: number;
  changedBy: string;
}

export type CatalogResult<T> =
  | { kind: "ok"; value: T }
  | { kind: "error"; message: string }
  // No session — the caller should send the visitor to sign in.
  | { kind: "unauthorized" };

async function call<T>(
  input: RequestInfo,
  init: RequestInit | undefined,
  unwrap: (payload: unknown) => T | undefined,
): Promise<CatalogResult<T>> {
  let res: Response;
  try {
    res = await fetch(input, { credentials: "include", ...init });
  } catch (err) {
    return { kind: "error", message: err instanceof Error ? err.message : "request failed (network)" };
  }

  if (res.status === 401) {
    return { kind: "unauthorized" };
  }

  let payload: unknown;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (!res.ok) {
    const apiError =
      payload && typeof payload === "object" && "error" in payload
        ? String((payload as { error: unknown }).error)
        : `${res.status} ${res.statusText}`.trim();
    return { kind: "error", message: apiError };
  }

  const value = unwrap(payload);
  if (value === undefined) {
    return { kind: "error", message: "unexpected response shape" };
  }
  return { kind: "ok", value };
}

export function fetchProducts(): Promise<CatalogResult<Product[]>> {
  return call<Product[]>("/api/products", undefined, (p) =>
    p && typeof p === "object" && "products" in p
      ? ((p as { products: Product[] }).products)
      : undefined,
  );
}

export function updatePrice(sku: string, unitPrice: number): Promise<CatalogResult<Product>> {
  return call<Product>(
    `/api/admin/products/${encodeURIComponent(sku)}/price`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ unitPrice }),
    },
    (p) => (p && typeof p === "object" && "product" in p ? (p as { product: Product }).product : undefined),
  );
}

export function updateReorderPoint(sku: string, reorderPoint: number): Promise<CatalogResult<Product>> {
  return call<Product>(
    `/api/admin/products/${encodeURIComponent(sku)}/reorder-point`,
    {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ reorderPoint }),
    },
    (p) => (p && typeof p === "object" && "product" in p ? (p as { product: Product }).product : undefined),
  );
}

export function fetchPriceHistory(sku: string): Promise<CatalogResult<PriceHistoryEntry[]>> {
  return call<PriceHistoryEntry[]>(
    `/api/admin/products/${encodeURIComponent(sku)}/price-history`,
    undefined,
    (p) =>
      p && typeof p === "object" && "history" in p
        ? ((p as { history: PriceHistoryEntry[] }).history)
        : undefined,
  );
}
